"use client";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import { useCookies } from "next-client-cookies";

import logo from "@/assets/img/logo/toshlima.jpg";
import Loader from "@/components/Loader";
import useCartInfo from "@/hooks/use-cart-info";
import useSticky from "@/hooks/use-sticky";
import ExtraInfo from "./header-com/extra-info";
import NavManus from "./header-com/nav-manus";
import SearchPopup from "./header-com/search-popup";

const OffCanvas = dynamic(() => import("@/components/common/offcanvas"), { ssr: false });
const MiniCart = dynamic(() => import("./header-com/mini-cart"), { ssr: false });

type IProps = {
  header_big?: boolean;
  white_bg?: boolean;
};

const Header = ({ header_big, white_bg }: IProps) => {
  const cookies = useCookies();
  const { sticky } = useSticky();
  const { quantity } = useCartInfo();
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [mounted, setMounted] = useState<boolean>(false);
  const [showCart, setShowCart] = useState<boolean>(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const isLoggedIn = mounted && cookies.get("userinfo") && cookies.get("token");

  const handleCartClick = () => {
    if (mounted && quantity > 0) setShowCart(!showCart);
  };

  return (
    <>
      {loading && <Loader />}

      <header className="w-full">
        <div
          id="header-sticky"
          className={`header__area ${header_big ? "box-25" : !white_bg ? "grey-bg" : ""} ${sticky ? "sticky" : ""}`}
          style={{ background: "#ffffff" }} // soft white background
        >
          <div className={`${header_big ? "container-fluid" : "container"}`}>
            <div className="row align-items-center">

              {/* Logo */}
             {/* Logo */}
<div className="col-xl-3 col-lg-3 col-md-4 col-sm-4 d-flex justify-content-center justify-content-md-start">
  <div className="logo">
    <Link href="/">
      {/* Desktop Logo */}
      <Image
        src={logo}
        alt="logo"
        priority
        width={220} // bigger desktop logo
        className="d-none d-md-block"
      />
      {/* Mobile Logo */}
      <Image
        src={logo}
        alt="logo"
        priority
        width={180} // mobile logo
        className="d-md-none"
      />
    </Link>
  </div>
</div>

              {/* Menu + Icons */}
              <div className="col-xl-9 col-lg-9 col-md-8 col-sm-8">
                <div className="header__right d-flex flex-column flex-md-row align-items-center justify-center justify-md-between gap-2 md:gap-0">

                  {/* Desktop Menu */}
                  <div className="main-menu d-none d-lg-block flex-1">
                    <nav>
                      <NavManus setLoading={setLoading} />
                    </nav>
                  </div>

                  {/* Actions */}
                  <div className="header__action d-flex align-items-center justify-center w-full md:w-auto gap-3 md:gap-4">
                    <ul className="d-flex align-items-center justify-center gap-3 md:gap-4 m-0 p-0 list-unstyled">

                      {/* Search */}
                      <li>
                        <button
                          className="btn-link border-0 bg-transparent p-2"
                          onClick={() => mounted && setShowSearch(true)}
                          style={{ color: "#4b0082" }}
                          aria-label="Search"
                        >
                          <i className="ion-ios-search-strong fs-5"></i>
                        </button>
                      </li>

                      {/* Cart */}
                      <li className="position-relative">
                        <button
                          className={`btn-link border-0 bg-transparent p-2 ${quantity === 0 ? "opacity-50" : ""}`}
                          style={{ color: "#4b0082", cursor: quantity === 0 ? "default" : "pointer" }}
                          aria-label="Shopping Cart"
                          onClick={handleCartClick}
                        >
                          <i className="ion-bag fs-5"></i>
                          {mounted && quantity > 0 && (
                            <span className="badge bg-purple-700 text-white rounded-pill ms-1">{quantity}</span>
                          )}
                        </button>
                        {mounted && quantity > 0 && showCart && <MiniCart setLoading={setLoading} />}
                      </li>

                      {/* User */}
                      <li>
                        <button
                          className="btn-link border-0 bg-transparent p-2"
                          style={{ color: "#4b0082" }}
                          aria-label="User Account"
                        >
                          {mounted ? (
                            isLoggedIn ? (
                              <i className="far fa-user fs-5"></i>
                            ) : (
                              <i className="fas fa-sign-in-alt fs-5"></i>
                            )
                          ) : (
                            <i className="fas fa-sign-in-alt fs-5"></i>
                          )}
                        </button>
                        {mounted && <ExtraInfo setLoading={setLoading} />}
                      </li>
                    </ul>

                    {/* Mobile Hamburger */}
                    <div className="mobile-menu-btn d-lg-none ms-2">
                      <button
                        onClick={() => mounted && setShowSidebar(true)}
                        className="btn-link border-0 bg-transparent p-2"
                        style={{ color: "#4b0082" }}
                        aria-label="Mobile Menu"
                      >
                        <i className="fas fa-bars fs-5"></i>
                      </button>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </div>
      </header>

      {/* Modals */}
      {mounted && (
        <>
          <SearchPopup showSearch={showSearch} setShowSearch={setShowSearch} />
          <OffCanvas openMobileMenus={showSidebar} setOpenMobileMenus={setShowSidebar} />
        </>
      )}
    </>
  );
};

export default Header;
